import classNames from 'classnames';
import { useCallback, useState } from 'react';
import styled from 'styled-components';

import Dialog from '@sorare/core/src/components/dialog';
import { Stepper } from '@sorare/core/src/components/onboarding/Dialog/Stepper';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { BuildYourClub } from './BuildYourClub';
import { CardDeck } from './CardDeck';
import { ConfirmStarSelection } from './ConfirmStarSelection';
import { SelectFavoriteTeam } from './SelectFavoriteTeam';
import { NBACommonStarPlayer, SelectStarPlayer } from './SelectStarPlayer';
import { StarterPack } from './StarterPack';
import { Welcome } from './Welcome';
import basketballCourt from './assets/baseballCourt.jpg';
import buildYourTeamBg from './assets/buildYourTeamBg.png';
import cardBg from './assets/cardBg.jpg';
import chaseBg from './assets/chaseBg.jpg';

const StepperWrapper = styled.div`
  width: 120px;
  margin: auto;
  margin-top: var(--double-unit);
`;

const StyledDialog = styled(Dialog)`
  position: relative;

  &.welcome {
    background-image: url(${basketballCourt});
  }

  &.welcome::before {
    inset: 0;
    content: '';
    position: absolute;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  &.chase {
    background-image: url(${chaseBg});
    background-size: cover;
    background-position: top;
  }

  &.build {
    background:
      linear-gradient(to top, #1e4ea9 20%, rgba(30, 78, 169, 0) 80%),
      url(${buildYourTeamBg}) no-repeat;
    background-size: cover;
  }

  &.finalSteps {
    background-image: url(${cardBg});
  }

  & > div {
    display: flex;
    flex-direction: column;
    @media ${laptopAndAbove} {
      min-height: min(calc(100vh - var(--unit) * 8), 800px);
    }
  }

  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const S3Onboarding = ({ isOpen, setIsOpen }: Props) => {
  const [step, setStep] = useState(0);
  const { postMessage, isMobileApp } = useIsMobileApp();
  const { currentUser } = useCurrentUserContext();
  const isOnboarded = currentUser?.nbaUserProfile?.onboarded;
  const [selectedPlayer, setSelectedPlayer] = useState<NBACommonStarPlayer>();
  const [selectedFavoriteTeam, setSelectedFavoriteTeam] = useState<
    string | undefined
  >();

  const onClose = useCallback(() => {
    if (isMobileApp) {
      postMessage('nbaOnboardingEventComplete');
    } else {
      setIsOpen(false);
    }
  }, [isMobileApp, postMessage, setIsOpen]);

  return (
    <StyledDialog
      hideHeader
      // We should only scroll when choosing a star player
      fullWidth
      disableBackdropClick
      scroll="paper"
      open={isOpen}
      maxWidth="xs"
      onClose={isOnboarded ? () => setIsOpen(false) : undefined}
      className={classNames({
        welcome: step === 0,
        chase: step === 1,
        build: step === 2 || step === 3,
        finalSteps: step > 3,
      })}
    >
      {step < 2 && (
        <StepperWrapper>
          <Stepper step={step} length={2} setStep={n => setStep(n)} />
        </StepperWrapper>
      )}
      {step > 3 && (
        <StepperWrapper>
          <Stepper step={step - 4} length={4} />
        </StepperWrapper>
      )}
      {step < 2 && <Welcome step={step as 0 | 1} setStep={setStep} />}
      {step === 2 && <BuildYourClub setStep={setStep} />}
      {step === 3 && (
        <SelectStarPlayer
          setStep={setStep}
          setSelectedPlayer={setSelectedPlayer}
        />
      )}
      {step === 4 && (
        <ConfirmStarSelection setStep={setStep} player={selectedPlayer} />
      )}
      {step === 5 && (
        <SelectFavoriteTeam
          setStep={setStep}
          selectedFavoriteTeam={selectedFavoriteTeam}
          setSelectedFavoriteTeam={setSelectedFavoriteTeam}
        />
      )}
      {step === 6 && <StarterPack setStep={setStep} />}
      {step === 7 && selectedFavoriteTeam && selectedPlayer?.anyPlayer.slug && (
        <CardDeck
          onClose={onClose}
          teamSlug={selectedFavoriteTeam}
          playerSlug={selectedPlayer.anyPlayer.slug}
        />
      )}
    </StyledDialog>
  );
};
